import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Input, PageContent } from "beproduct-ui";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/navigation/page-header";

// Minimal valid Telerik Reporting v20 .trdp body (xmlns 2026/2.0).
// Kept as a hard-coded template since the alternative — calling
// Telerik's serializer on the server for a one-off blank — adds a
// round-trip and a backend endpoint for no gain. If the schema bumps
// in a future Telerik upgrade, update this string and re-test render.
const BLANK_TRDP_XML = `<?xml version="1.0" encoding="utf-8"?>
<Report Width="17cm" Name="Untitled" xmlns="http://schemas.telerik.com/reporting/2026/2.0">
  <Items>
    <DetailSection Height="1in" Name="detail1" />
  </Items>
</Report>`;

export const Route = createFileRoute("/_authed/$companySlug/reports/new")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  component: NewReportPage,
});

function NewReportPage() {
  const { company } = Route.useRouteContext();
  const { companySlug } = Route.useParams();
  const nav = useNavigate({ from: "/$companySlug/reports/new" });
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api<{ id: string }>(
        `/api/tenants/${encodeURIComponent(company.id)}/reports/reports`,
        {
          method: "POST",
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            definition: { xml: BLANK_TRDP_XML },
          }),
        },
      );
      qc.invalidateQueries({ queryKey: ["reports", company.id] });
      nav({
        to: "/$companySlug/reports/$reportId",
        params: { companySlug, reportId: res.id },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 max-w-xl space-y-6">
        <PageHeader title="New report" />
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="r-name">Name</label>
              <Input
                id="r-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Style Color Library"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="r-desc">Description (optional)</label>
              <Input
                id="r-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this report for?"
              />
            </div>
            {err && <div className="text-sm text-destructive">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() =>
                  nav({ to: "/$companySlug/reports", params: { companySlug } })
                }
                disabled={busy}
              >
                Cancel
              </Button>
              <Button onClick={create} disabled={busy || !name.trim()}>
                {busy ? "Creating…" : "Create"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContent>
  );
}
